import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled';
import type { TriggerProps } from '@atlaskit/popup';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAgentsList } from '@atlassian/conversation-assistant-agent';
import type { Agent } from '@atlassian/conversation-assistant-service/types';

import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { AgentPresenceMenu } from './AgentPresenceMenu.tsx';
import { AgentPresenceAvatarGroup } from './AgentPresenceAvatarGroup.tsx';

const i18n = defineMessages({
	agentGroupLabel: {
		id: 'team-presence.agent-group.label',
		defaultMessage: 'Active agents on content',
		description: 'Accessible region landmark for agent presence avatar group',
	},
});

const getUniqueAgents = (agents: (Agent | undefined)[]) => {
	const uniqueAgents = new Map<string, Agent>();

	agents?.forEach((agent) => {
		if (agent) {
			uniqueAgents.set(agent.id, agent);
		}
	});

	return Array.from(uniqueAgents.values());
};

export const AgentPresenceComponent = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { cloudId } = useSessionData();
	const [{ collabEditProvider }] = useNativeCollabState();
	const [activeAgents, setActiveAgents] = useState<Agent[]>([]);
	const [isAgentGroupOverflowOpen, setIsAgentGroupOverflowOpen] = useState(false);

	const allAgentsFromAssistanceService = useAgentsList({
		assistanceServiceParams: {
			siteId: cloudId,
			product: 'confluence',
			experienceId: 'ai-mate',
			touchpointSource: 'aiAgentPresence',
		},
	});

	useEffect(() => {
		const handleUpdatePresence = () => {
			const agents = collabEditProvider?.getAIProviderParticipants().map((agent) => {
				const agentId = agent.userId.replace('agent:', '');

				return allAgentsFromAssistanceService.find((a) => a.id === agentId);
			});

			setActiveAgents(getUniqueAgents(agents || []));
		};

		handleUpdatePresence();

		collabEditProvider?.on('presence', handleUpdatePresence);

		return () => {
			collabEditProvider?.off('presence', handleUpdatePresence);
		};
	}, [allAgentsFromAssistanceService, collabEditProvider]);

	const handleAgentGroupOverflowMenuClick = useCallback(
		(agentCount: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'aiAgentPresence',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'aiAgentPresenceGroupOverflow',
					product: 'confluence',
					attributes: {
						agents: agentCount,
					},
				},
			}).fire();

			setIsAgentGroupOverflowOpen(
				(isAgentGroupOverflowOpenState) => !isAgentGroupOverflowOpenState,
			);
		},
		[createAnalyticsEvent, setIsAgentGroupOverflowOpen],
	);

	const handleAgentGroupOverflowMenuClose = useCallback(() => {
		setIsAgentGroupOverflowOpen(false);
	}, [setIsAgentGroupOverflowOpen]);

	const renderAgentGroupOverflowMenu = useCallback(() => {
		return <AgentPresenceMenu agents={activeAgents} />;
	}, [activeAgents]);

	if (!activeAgents?.length) {
		return null;
	}

	return (
		<Popup
			placement="bottom-end"
			isOpen={isAgentGroupOverflowOpen}
			onClose={handleAgentGroupOverflowMenuClose}
			content={renderAgentGroupOverflowMenu}
			trigger={(triggerProps: TriggerProps) => (
				<Box
					{...triggerProps}
					paddingInline="space.025"
					aria-label={formatMessage(i18n.agentGroupLabel)}
					role="region"
				>
					<AgentPresenceAvatarGroup
						agents={activeAgents}
						isOpen={isAgentGroupOverflowOpen}
						onMoreClick={() => handleAgentGroupOverflowMenuClick(activeAgents.length)}
					/>
				</Box>
			)}
		/>
	);
};

export const AgentPresence = () => {
	return null;
};
